import React from 'react';
import { Sparkles, MessageCircle, Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pricing } from '@config/pricing';

const HeroSection: React.FC = () => {
    return (
        <section className="relative pt-10 md:pt-32 pb-24 md:pb-32 overflow-hidden">
            {/* Animated Background Orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 blur-[120px] rounded-full animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-600/15 blur-[100px] rounded-full animate-pulse [animation-delay:1s]" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/10 blur-[140px] rounded-full" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* Left Content */}
                        <div className="text-center lg:text-left space-y-8">
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20 backdrop-blur-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                </span>
                                <span className="text-xs font-bold text-orange-200 uppercase tracking-wider">Welcome Gift</span>
                                <div className="w-px h-4 bg-white/20" />
                                <span className="text-xs font-bold text-white">{pricing.freeQuestions} Questions Free worth {pricing.currency}{pricing.signupReward}</span>
                                <Sparkles size={14} className="text-orange-400" />
                            </div>

                            {/* Main Heading */}
                            <div className="space-y-4">
                                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight">
                                    <span className="block text-white font-serif">
                                        Your Personal
                                    </span>
                                    <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 font-serif">
                                        AI Astrologer
                                    </span>
                                </h1>
                                <p className="text-lg sm:text-xl text-purple-200/80 max-w-xl mx-auto lg:mx-0 leading-relaxed font-sans">
                                    Get instant cosmic insights with <span className="font-bold text-purple-300">15+ AI-powered services</span> including Vedic astrology, tarot, numerology & more. Available in <span className="font-bold text-purple-300">10+ languages</span>.
                                </p>
                            </div>

                            {/* CTA Button */}
                            <div className="flex justify-center lg:justify-start">
                                <Link
                                    to="/login"
                                    className="group relative w-full sm:w-auto sm:min-w-[400px] px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl text-white font-bold text-sm uppercase tracking-wider hover:scale-105 transition-all shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 flex items-center justify-center gap-2 overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <MessageCircle size={18} className="relative z-10" />
                                    <span className="relative z-10">Start Free Chat</span>
                                    <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>

                            {/* Trust Indicators */}
                            <div className="flex flex-wrap items-center gap-6 justify-center lg:justify-start pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex -space-x-3">
                                        <div className="w-10 h-10 rounded-full border-2 border-cosmic-950 overflow-hidden ring-2 ring-purple-500/30 transition-transform hover:scale-110 hover:z-10">
                                            <img src="/user_avatar_1.webp" alt="BhriguGPT satisfied user 1" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="w-10 h-10 rounded-full border-2 border-cosmic-950 overflow-hidden ring-2 ring-purple-500/30 transition-transform hover:scale-110 hover:z-10">
                                            <img src="/user_avatar_2.webp" alt="BhriguGPT satisfied user 2" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="w-10 h-10 rounded-full border-2 border-cosmic-950 overflow-hidden ring-2 ring-purple-500/30 transition-transform hover:scale-110 hover:z-10">
                                            <img src="/user_avatar_3.webp" alt="BhriguGPT satisfied user 3" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="w-10 h-10 rounded-full border-2 border-cosmic-950 overflow-hidden ring-2 ring-purple-500/30 transition-transform hover:scale-110 hover:z-10">
                                            <img src="/user_avatar_4.webp" alt="BhriguGPT satisfied user 4" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="w-10 h-10 rounded-full border-2 border-cosmic-950 overflow-hidden ring-2 ring-purple-500/30 transition-transform hover:scale-110 hover:z-10">
                                            <img src="/user_avatar_5.webp" alt="BhriguGPT satisfied user 5" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                    <div className="text-sm">
                                        <div className="text-white font-bold">10,000+</div>
                                        <div className="text-purple-300/60 text-xs">Happy Users</div>
                                    </div>
                                </div>
                                <div className="w-px h-8 bg-white/10" />
                                <div className="text-sm">
                                    <div className="flex items-center gap-1 text-orange-400 mb-1">
                                        <Star size={14} fill="currentColor" />
                                        <Star size={14} fill="currentColor" />
                                        <Star size={14} fill="currentColor" />
                                        <Star size={14} fill="currentColor" />
                                        <Star size={14} fill="currentColor" />
                                    </div>
                                    <div className="text-purple-300/60 text-xs">4.9/5 Rating</div>
                                </div>
                            </div>
                        </div>

                        {/* Right - Chat Mockup */}
                        <div className="relative lg:scale-110">
                            {/* Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-pink-600/20 to-orange-600/20 blur-[100px] rounded-full" />

                            {/* Chat Interface */}
                            <div className="relative bg-white/[0.06] backdrop-blur-2xl border border-white/[0.12] rounded-3xl shadow-2xl overflow-hidden">
                                {/* Header */}
                                <div className="bg-white/[0.04] border-b border-white/[0.08] px-6 py-4 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600/80 to-purple-700/80 flex items-center justify-center overflow-hidden border-2 border-purple-400/30 shadow-lg shadow-purple-500/20">
                                        <img src="/bhrigoo_rishi.webp" alt="BhriguGPT" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-bold text-white font-serif">BhriguGPT</div>
                                        <div className="flex items-center gap-1.5 text-xs text-purple-300/70">
                                            <span className="relative flex h-2 w-2">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                            </span>
                                            Online
                                        </div>
                                    </div>
                                </div>

                                {/* Messages */}
                                <div className="p-6 space-y-4 min-h-[400px]">
                                    {/* AI Message */}
                                    <div className="flex gap-3 max-w-[85%]">
                                        <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl rounded-tl-none px-4 py-3 text-sm text-purple-100/90 leading-relaxed">
                                            Namaste! 🙏 I've analyzed your birth chart. Your Jupiter transit indicates excellent opportunities in career this month.
                                        </div>
                                    </div>

                                    {/* User Message */}
                                    <div className="flex gap-3 max-w-[85%] ml-auto">
                                        <div className="bg-gradient-to-br from-violet-600 to-indigo-600 rounded-2xl rounded-tr-none px-4 py-3 text-sm text-white leading-relaxed shadow-lg shadow-violet-500/20">
                                            That's amazing! Should I start my new business now? 🌟
                                        </div>
                                    </div>

                                    {/* Thinking State */}
                                    <div className="flex gap-3 max-w-[85%]">
                                        <div className="bg-white/[0.06] backdrop-blur-sm border border-violet-500/20 rounded-2xl px-4 py-3 flex items-center gap-3">
                                            <div className="flex gap-1">
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                                <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce"></span>
                                            </div>
                                            <span className="text-xs text-purple-300/70 font-medium">Consulting cosmic wisdom...</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Input */}
                                <div className="bg-white/[0.03] border-t border-white/[0.08] px-6 py-4 flex items-center gap-3">
                                    <div className="flex-1 bg-white/[0.05] border border-white/[0.1] rounded-full px-4 py-3 text-sm text-purple-300/50">
                                        Ask about your future...
                                    </div>
                                    <button className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/25 hover:scale-105 transition-transform">
                                        <ArrowRight size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
